import { useQuery, useQueries, useMutation, useQueryClient } from '@tanstack/react-query';

import getTeam from '@entities/team/api/team';
import {
  getTeamInfo,
  updateTeam,
  addTeam,
  getTeamByadmin,
  getAdminDisabledTeamList,
} from '@entities/team/api/team';
/**
 * 사용자의 사용중인 부서만 조회
 * @param {} companyId
 * @param {*} options
 * @returns
 */
function useGetTeamQuery(companyId, options = {}) {
  return useQuery({
    queryKey: ['company', companyId, 'teamList'],
    queryFn: () => getTeam(companyId),
    enabled: !!companyId,
    // 팀 목록은 자주 변경되지 않으므로 30분간 캐싱
    staleTime: 1000 * 60 * 30,
    ...options,
  });
}

export default useGetTeamQuery;

// 부서 다건 조회
export function useGetTeamsByCompanies(companies = []) {
  const queries = useQueries({
    queries: companies.map((company) => ({
      queryKey: ['company', company.companyCode, 'teamList'],
      queryFn: () => getTeam(company.companyCode),
      enabled: !!company.companyCode,
      staleTime: 1000 * 60 * 30,
      select: (res) => res?.resultData?.teamList ?? [],
    })),
  });

  return queries;
}
/**
 * 팀 상세 정보 조회
 * @param {number} teamCode - 팀 코드
 * @param {boolean} toHtml - HTML 변환 여부 (선택)
 * @param {object} options - React Query 옵션
 */
export function useGetTeamInfoQuery(teamCode, toHtml = false, options = {}) {
  return useQuery({
    queryKey: ['team', teamCode, 'info'],
    queryFn: () => getTeamInfo(teamCode, toHtml),
    enabled: !!teamCode,
    select: (res) => res?.resultData ?? null,
    staleTime: 1000 * 60 * 10, // 상세 정보는 10분 캐싱
    ...options,
  });
}
/**
 * 부서 수정 Mutation
 */
export function useUpdateTeamMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateTeam,

    onSuccess: (_, variables) => {
      const { teamCode, company } = variables;

      // 팀 상세 정보 갱신
      if (teamCode) {
        queryClient.invalidateQueries({
          queryKey: ['team', teamCode, 'info'],
        });
      }

      // 회사별 팀 목록 갱신
      if (company) {
        queryClient.invalidateQueries({
          queryKey: ['company', company, 'teamList'],
        });

        // 3️ 관리자 팀 관련 전체 (admin-all, admin-disabled)
        queryClient.invalidateQueries({
          queryKey: ['company', company, 'team'],
        });
      }
    },
  });
}
/**
 *  부서 추가 Mutation
 */
export function useAddTeamMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: addTeam,

    onSuccess: (_, variables) => {
      const { company } = variables;

      // 회사별 팀 목록 갱신 (신규 부서 반영)
      if (company) {
        queryClient.invalidateQueries({
          queryKey: ['company', company, 'teamList'],
        });

        // 3️관리자 팀 관련 전체 (admin-all, admin-disabled)
        queryClient.invalidateQueries({
          queryKey: ['company', company, 'team'],
        });
      }
    },
  });
}
/**
 * 관리자의 모든 부서 조회
 * @param {*} companyId
 * @param {*} options
 * @returns
 */
export function useGetTeamByadminQuery(companyId, options = {}) {
  return useQuery({
    queryKey: ['company', companyId, 'team', 'admin-all'],
    queryFn: () => getTeamByadmin(companyId),
    enabled: !!companyId,
    // 팀 목록은 자주 변경되지 않으므로 30분간 캐싱
    // staleTime: 1000 * 60 * 30,
    ...options,
  });
}
/**
 * 관리자의 비활성 부서 조회
 */
export function useAdminDisabledTeamListQuery(companyId, options = {}) {
  return useQuery({
    queryKey: ['company', companyId, 'team', 'admin-disabled'],
    queryFn: () => getAdminDisabledTeamList(companyId),
    enabled: !!companyId,
    // 팀 목록은 자주 변경되지 않으므로 30분간 캐싱
    // staleTime: 1000 * 60 * 30,
    ...options,
  });
}
